/**
 * @fileoverview Super-admin feedback submissions viewer page.
 *
 * Read-mostly viewer over the `feedback_submissions` table. Renders a filter
 * form + a paginated results table. Status can be updated inline via a select.
 * Screenshot thumbnails open in a full-size Modal.js overlay. Expand rows show
 * breadcrumbs and browser_info JSON plus attachment thumbnails.
 *
 * Mounted under `${adminPath}/feedback` (super-admin IP whitelist + role gate
 * applies transparently at the App routing level).
 *
 * @module pages/FeedbackAdmin
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';

/**
 * Renders an `<img>` for one of the super-admin authenticated endpoints
 * (`/screenshot` or `/attachment/:n`) by fetching via axios (auto-adds
 * Bearer) and swapping to an object URL. Necessary because browsers do
 * NOT attach the Authorization header on plain `<img src=url>` loads
 * (Mark UAT 2026-08-28: all screenshot thumbnails were 401'ing).
 *
 * Takes stable primitive props (not a fetcher function) so this
 * component can safely appear inside a `.map()` loop without tripping
 * the hooks-in-loops rule. The object URL is revoked on unmount.
 *
 * @param {string} props.kind - 'screenshot' or 'attachment'.
 * @param {string} props.adminApiPrefix
 * @param {number} props.feedbackId
 * @param {number} [props.attachmentIndex] - Required when kind='attachment'.
 * @param {string} props.alt
 * @param {string} [props.className]
 */
function AuthImg({ kind, adminApiPrefix, feedbackId, attachmentIndex, alt, className }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    const promise = kind === 'attachment'
      ? api.fetchFeedbackAttachmentBlobUrl(adminApiPrefix, feedbackId, attachmentIndex)
      : api.fetchFeedbackScreenshotBlobUrl(adminApiPrefix, feedbackId);
    // Guard revokeObjectURL — jsdom (Jest's DOM) doesn't ship it.
    const revoke = (u) => {
      if (u && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(u);
      }
    };
    promise
      .then(u => {
        if (cancelled) { revoke(u); return; }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => {
      cancelled = true;
      revoke(objectUrl);
    };
  }, [kind, adminApiPrefix, feedbackId, attachmentIndex]);
  if (err) return <span className="feedback-admin__thumb-error" aria-label={alt}>×</span>;
  if (!url) return <span className="feedback-admin__thumb-loading" aria-label={`Loading ${alt}`} />;
  return <img src={url} alt={alt} className={className} loading="lazy" />;
}

const DEFAULT_PER_PAGE = 25;

const EMPTY_FILTERS = {
  category: '',
  gh_state: '',
  date_from: '',
  date_to: '',
  user_email: '',
  org_id: '',
};

const CATEGORY_OPTIONS = ['bug', 'suggestion', 'compliment', 'question'];
// Values match the server-side `?gh_state=` filter keywords. GH state
// options replaced the local `status` dropdown on 2026-08-26 — cogslog
// no longer owns feedback triage; GitHub Issues is the source of truth.
const GH_STATE_FILTER_OPTIONS = [
  { value: 'open', label: 'Open on GitHub' },
  { value: 'closed', label: 'Closed on GitHub' },
  { value: 'unmirrored', label: 'Not mirrored' },
];

/**
 * Derive the human-readable CRM state label from a linked-entity payload.
 * Returned as a tuple `[label, tone]` where `tone` is one of
 * `mapped`|`unmapped`|`failing` — used by the sidebar CSS to colour the dot.
 *
 * @param {Object} data - The /linked-entity response body.
 * @returns {[string, 'mapped'|'unmapped'|'failing']}
 */
function deriveCrmState(data) {
  if (data && data.last_error) return ['Failing', 'failing'];
  if (data && data.crm_id) return ['Mapped', 'mapped'];
  return ['Unmapped', 'unmapped'];
}

/**
 * Human-readable GH-thread error message from the backend's tagged error string.
 * The backend always returns HTTP 200 for graceful errors, tagged by
 * `error` in the payload — the frontend never sees a raw 5xx here.
 *
 * @param {string} err - The `error` field from the backend response.
 * @param {number} [issueNumber] - The mirrored issue number, if known.
 * @returns {string} A short, operator-friendly reason to render inline.
 */
function ghThreadErrorReason(err, issueNumber) {
  if (err === 'github_token_not_configured') {
    return 'GH mirror is disabled (GITHUB_TOKEN not set in container env).';
  }
  if (err === 'issue_missing_or_deleted') {
    return `Issue #${issueNumber || '?'} was deleted, transferred, or is not visible to the mirror token.`;
  }
  if (err === 'no_linked_issue') {
    return 'This feedback row was never mirrored to a GitHub Issue.';
  }
  return 'GitHub API returned an error. Try again in a few minutes.';
}

/**
 * Sub-component: the linked GitHub Issue thread rendered inside a feedback
 * row's expand panel. Fetches lazily on mount (which only happens when
 * the row is expanded, per the parent's conditional render). Shows a
 * skeleton while loading, the issue body + all comments on success, and
 * a gentle inline message on error (never blows up the row).
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Admin API prefix from AuthContext.
 * @param {number} props.feedbackId - Feedback submission id.
 * @param {number} props.issueNumber - The mirrored GH issue number
 *   (from the row's `gh_issue_number` cache column).
 * @returns {JSX.Element}
 */
function GithubThreadPanel({ adminApiPrefix, feedbackId, issueNumber }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    api
      .fetchFeedbackGithubThread(adminApiPrefix, feedbackId)
      .then((resp) => {
        if (cancelled) return;
        setState({ loading: false, data: resp?.data || resp || null, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const backendErr = err?.response?.data?.error;
        setState({
          loading: false,
          data: null,
          error: backendErr || 'github_error',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [adminApiPrefix, feedbackId]);

  const heading = `GitHub Issue #${issueNumber}`;

  if (state.loading) {
    return (
      <section
        className="feedback-admin__gh-thread"
        data-testid="feedback-gh-thread"
        aria-label={heading}
      >
        <h4 className="feedback-admin__gh-thread-heading">{heading}</h4>
        <div
          className="feedback-admin__gh-thread-loading"
          role="status"
          aria-live="polite"
          data-testid="feedback-gh-thread-loading"
        >
          <div className="feedback-admin__gh-thread-skeleton" />
          <div className="feedback-admin__gh-thread-skeleton feedback-admin__gh-thread-skeleton--short" />
          <div className="feedback-admin__gh-thread-skeleton" />
          <span className="sr-only">Loading GitHub thread…</span>
        </div>
      </section>
    );
  }

  // The backend returns HTTP 200 for the "graceful degrade" family
  // (token unset, issue deleted, upstream 5xx…) with `ok: false` +
  // an `error` tag. A network / 404 that landed in the catch above
  // also lands here via state.error.
  const data = state.data;
  const backendErr = state.error || (data && data.ok === false ? data.error : null);

  if (backendErr) {
    return (
      <section
        className="feedback-admin__gh-thread"
        data-testid="feedback-gh-thread"
        aria-label={heading}
      >
        <h4 className="feedback-admin__gh-thread-heading">{heading}</h4>
        <p
          className="feedback-admin__gh-thread-error"
          role="status"
          aria-live="polite"
          data-testid="feedback-gh-thread-error"
        >
          GitHub thread unavailable — {ghThreadErrorReason(backendErr, issueNumber)}
        </p>
      </section>
    );
  }

  const issue = (data && data.issue) || {};
  const comments = Array.isArray(data && data.commentsError ? [] : data && data.comments)
    ? data.comments
    : [];
  const stateLabel = issue.state === 'closed' ? 'Closed' : 'Open';

  return (
    <section
      className="feedback-admin__gh-thread"
      data-testid="feedback-gh-thread"
      aria-label={heading}
    >
      <header className="feedback-admin__gh-thread-header">
        <h4 className="feedback-admin__gh-thread-heading">
          {heading}
          <span
            className={`badge feedback-admin__gh-thread-state feedback-admin__gh-thread-state--${issue.state || 'unknown'}`}
            data-testid="feedback-gh-thread-state"
          >
            {stateLabel}
          </span>
        </h4>
        {issue.html_url ? (
          <a
            className="btn btn-ghost btn-xs feedback-admin__gh-thread-link"
            href={issue.html_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="feedback-gh-thread-open-link"
          >
            Open on GitHub
          </a>
        ) : null}
      </header>

      {issue.title ? (
        <p className="feedback-admin__gh-thread-title" data-testid="feedback-gh-thread-title">
          {issue.title}
        </p>
      ) : null}

      <article
        className="feedback-admin__gh-thread-body"
        data-testid="feedback-gh-thread-body"
      >
        <GhCommentMeta user={issue.user} createdAt={issue.created_at} />
        <div className="feedback-admin__gh-thread-md">
          {issue.body ? (
            <ReactMarkdown skipHtml>{issue.body}</ReactMarkdown>
          ) : (
            <em className="feedback-admin__gh-thread-empty">(empty body)</em>
          )}
        </div>
      </article>

      <div
        className="feedback-admin__gh-thread-comments"
        data-testid="feedback-gh-thread-comments"
      >
        {comments.length === 0 ? (
          <p className="feedback-admin__gh-thread-nocomments">No comments yet.</p>
        ) : (
          comments.map((c) => (
            <article
              key={c.id}
              className="feedback-admin__gh-thread-comment"
              data-testid="feedback-gh-thread-comment"
            >
              <GhCommentMeta user={c.user} createdAt={c.created_at} htmlUrl={c.html_url} />
              <div className="feedback-admin__gh-thread-md">
                <ReactMarkdown skipHtml>{c.body || ''}</ReactMarkdown>
              </div>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

/**
 * Compact "avatar · login · timestamp" header shown above each GH body /
 * comment inside the GithubThreadPanel.
 *
 * @param {Object} props
 * @param {Object} [props.user] - GH user shape ({login, avatar_url, html_url}).
 * @param {string} [props.createdAt] - ISO8601 timestamp string.
 * @param {string} [props.htmlUrl] - Optional link to the comment on GitHub.
 * @returns {JSX.Element}
 */
function GhCommentMeta({ user, createdAt, htmlUrl }) {
  const login = (user && user.login) || 'unknown';
  return (
    <div className="feedback-admin__gh-thread-meta">
      {user && user.avatar_url ? (
        <img
          src={user.avatar_url}
          alt=""
          aria-hidden="true"
          className="feedback-admin__gh-thread-avatar"
          loading="lazy"
        />
      ) : (
        <span className="feedback-admin__gh-thread-avatar feedback-admin__gh-thread-avatar--placeholder" aria-hidden="true" />
      )}
      <span className="feedback-admin__gh-thread-login">{login}</span>
      {createdAt ? (
        <span className="feedback-admin__gh-thread-ts">
          · {new Date(createdAt).toLocaleString()}
        </span>
      ) : null}
      {htmlUrl ? (
        <a
          className="feedback-admin__gh-thread-permalink"
          href={htmlUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open comment by ${login} on GitHub`}
        >
          #
        </a>
      ) : null}
    </div>
  );
}

/**
 * Sub-component: the linked-entity + CRM sync sidebar rendered inside a
 * feedback row's expand panel. Fetches its data lazily when the row expands.
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Admin API prefix from AuthContext.
 * @param {number} props.feedbackId - Feedback submission id.
 * @param {(entityType:string, entityId:number) => Promise<void>} props.onRetry
 *   Invoked when the operator clicks Retry sync; parent owns the API call
 *   and returns a Promise so the button can show its loading state.
 * @returns {JSX.Element|null}
 */
function LinkedEntitySidebar({ adminApiPrefix, feedbackId, onRetry }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    api
      .getFeedbackLinkedEntity(adminApiPrefix, feedbackId)
      .then((resp) => {
        if (cancelled) return;
        setState({ loading: false, data: resp?.data || resp || null, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          data: null,
          error: err?.response?.data?.error || 'Failed to load linked entity',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [adminApiPrefix, feedbackId]);

  /**
   * Click handler for the Retry sync button. Delegates to the parent-supplied
   * onRetry Promise then refetches this sidebar's data so the panel reflects
   * the new sync state without a page reload.
   */
  async function handleRetry() {
    if (retrying || !state.data) return;
    setRetrying(true);
    try {
      await onRetry(state.data.entity_type, state.data.entity_id);
      // Refetch so success / new error state renders.
      try {
        const resp = await api.getFeedbackLinkedEntity(adminApiPrefix, feedbackId);
        setState({ loading: false, data: resp?.data || resp || null, error: null });
      } catch {
        /* keep previous data on refetch failure */
      }
    } finally {
      setRetrying(false);
    }
  }

  if (state.loading) {
    return (
      <aside
        className="feedback-admin__linked-sidebar"
        data-testid="feedback-linked-entity-sidebar"
        aria-label="Linked entity and CRM sync"
      >
        <div className="feedback-admin__linked-sidebar-loading" role="status" aria-live="polite">
          Loading linked entity…
        </div>
      </aside>
    );
  }

  if (state.error) {
    return (
      <aside
        className="feedback-admin__linked-sidebar"
        data-testid="feedback-linked-entity-sidebar"
        aria-label="Linked entity and CRM sync"
      >
        <p className="err-msg" role="status" aria-live="polite">{state.error}</p>
      </aside>
    );
  }

  const data = state.data;
  if (!data || !data.linked) {
    // Component only rendered by callers when linkage exists, but guard
    // defensively so a race between the row cache and the /linked-entity
    // fetch degrades to hiding the panel rather than a blank card.
    return null;
  }

  const [stateLabel, stateTone] = deriveCrmState(data);
  const idClass = 'feedback-admin__linked-id';
  const canOpenCrm = !!data.crm_web_url;

  return (
    <aside
      className="feedback-admin__linked-sidebar"
      data-testid="feedback-linked-entity-sidebar"
      aria-label="Linked entity and CRM sync"
    >
      <h4 className="feedback-admin__linked-heading">Linked entity &amp; CRM sync</h4>

      <dl className="feedback-admin__linked-meta">
        <dt>Entity</dt>
        <dd>
          <span className="badge">{data.entity_type}</span>{' '}
          <span className={idClass}>#{data.entity_id}</span>
          {data.display_name ? (
            <>
              {' — '}
              <span className="feedback-admin__linked-name">{data.display_name}</span>
            </>
          ) : null}
        </dd>

        <dt>CRM sync</dt>
        <dd>
          <span
            className={`feedback-admin__linked-state feedback-admin__linked-state--${stateTone}`}
            data-testid="feedback-linked-crm-state"
          >
            <span className="feedback-admin__linked-dot" aria-hidden="true" />
            {stateLabel}
          </span>
          {data.crm_id ? (
            <div>
              <span className="feedback-admin__linked-label">Twenty {data.crm_object || 'record'}:</span>{' '}
              <span className={idClass}>{data.crm_id}</span>
            </div>
          ) : (
            <div className="feedback-admin__linked-hint">
              Never synced — click Retry sync to try now.
            </div>
          )}
          {data.last_error ? (
            <div className="feedback-admin__linked-error" title={data.last_error}>
              <strong>Last error:</strong>{' '}
              {data.last_error.length > 200
                ? `${data.last_error.slice(0, 200)}…`
                : data.last_error}
            </div>
          ) : null}
        </dd>
      </dl>

      <div className="feedback-admin__linked-actions">
        <button
          type="button"
          className={`btn btn-sm btn-secondary${retrying ? ' btn-loading' : ''}`}
          onClick={handleRetry}
          disabled={retrying}
          aria-label={`Retry CRM sync for ${data.entity_type} ${data.display_name || data.entity_id}`}
          data-testid="feedback-linked-retry-btn"
        >
          {retrying ? 'Retrying…' : 'Retry sync'}
        </button>
        {canOpenCrm ? (
          <a
            className="btn btn-sm btn-ghost"
            href={data.crm_web_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View ${data.entity_type} ${data.display_name || data.entity_id} in Twenty CRM`}
            data-testid="feedback-linked-view-crm-btn"
          >
            View in Twenty
          </a>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            disabled
            aria-label="Twenty deep-link unavailable — no CRM mapping yet"
            data-testid="feedback-linked-view-crm-btn"
          >
            View in Twenty
          </button>
        )}
      </div>
    </aside>
  );
}

/**
 * Remove empty-string filter values so the backend only sees applied filters.
 *
 * @param {Object} filters - Raw filter state from the form.
 * @returns {Object} Compact filters with empty strings removed.
 */
function compactFilters(filters) {
  const out = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) out[k] = v;
  });
  return out;
}

/**
 * Truncate a string to at most `max` characters with an ellipsis.
 *
 * @param {string} str - Input string.
 * @param {number} [max=120] - Maximum character length before truncation.
 * @returns {string} Truncated string.
 */
function truncate(str, max = 120) {
  if (!str) return '';
  return str.length > max ? `${str.slice(0, max)}…` : str;
}

/**
 * Super-admin feedback submissions page.
 *
 * @returns {JSX.Element}
 */
function FeedbackAdmin() {
  const { adminApiPrefix, isSuperAdmin } = useAuth();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [screenshotModalId, setScreenshotModalId] = useState(null);
  // GH state bulk-refresh button state — the panel talks to
  // POST /admin/feedback/refresh-gh-state, which walks every mirrored row
  // and updates the cached gh_issue_state column.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const headingRef = useRef(null);

  // Focus heading on mount for screen-reader / keyboard users (CLAUDE.md rule).
  useEffect(() => {
    if (headingRef.current) {
      headingRef.current.focus();
    }
  }, []);

  /**
   * Fetch feedback submissions from the backend, respecting current filters and page.
   */
  const fetchFeedback = useCallback(async () => {
    if (!adminApiPrefix) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...compactFilters(appliedFilters),
        page,
        per_page: DEFAULT_PER_PAGE,
      };
      const resp = await api.getFeedback(adminApiPrefix, params);
      const body = resp?.data || resp || {};
      setRows(body.items || []);
      setTotal(body.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load feedback');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [adminApiPrefix, appliedFilters, page]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  /**
   * Handle filter form field changes.
   *
   * @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement>} e
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Apply current filter values and reset to page 1.
   *
   * @param {React.FormEvent} e
   */
  const handleApply = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  /**
   * Reset all filters and return to page 1.
   */
  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  /**
   * Bulk refresh gh_issue_state for every mirrored row via the admin endpoint.
   * Cogslog no longer owns feedback triage — GitHub Issues is the source of
   * truth (see routes/feedback.py::refresh_feedback_gh_state). Refetches
   * the list on success so the freshly-cached states show up.
   */
  async function handleRefreshGhState() {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const { data } = await api.refreshFeedbackGhState(adminApiPrefix);
      setRefreshResult({
        ok: true,
        text: `Refreshed ${data.refreshed}/${data.attempted} · ${data.closed_now} closed on GH since last sync · ${data.failed} failed`,
      });
      await fetchFeedback();
    } catch (err) {
      setRefreshResult({
        ok: false,
        text: err?.response?.data?.error === 'gh disabled'
          ? 'GH mirror is disabled (GITHUB_TOKEN not set in container env).'
          : 'Refresh failed — see admin console for details.',
      });
    } finally {
      setRefreshing(false);
    }
  }

  /**
   * Toggle expanded detail row for a submission.
   *
   * @param {number} id - Submission ID.
   */
  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  /**
   * Handler passed to LinkedEntitySidebar so the Retry sync button can call
   * the CRM per-row resync endpoint. Kept at the page level (not inside the
   * sidebar) so tests can mock `api.crmResync` alongside the other API
   * mocks and so a future "Retry all failing linked entities" bulk action
   * can reuse the same call site.
   *
   * @param {string} entityType - One of user/organization/business/vendor/invoice.
   * @param {number} entityId - The cogslog row id.
   * @returns {Promise<Object|void>} Backend response body on success (the
   *   sidebar refetches its own state — no return value is required for it).
   */
  async function handleRetryCrmSync(entityType, entityId) {
    if (!adminApiPrefix) return undefined;
    return api.crmResync(adminApiPrefix, entityType, entityId);
  }

  // Super-admin guard — defense in depth beyond the router-level ProtectedRoute.
  if (isSuperAdmin === false) {
    return (
      <div className="page">
        <h1>Feedback</h1>
        <div className="empty-state" role="alert">
          <div className="empty-state__title">Access denied</div>
          <p className="empty-state__description">
            This page is restricted to system administrators.
          </p>
        </div>
      </div>
    );
  }

  const screenshotRow = screenshotModalId
    ? rows.find((r) => r.id === screenshotModalId)
    : null;

  return (
    <div className="page feedback-admin">
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="feedback-admin__heading"
      >
        Feedback Submissions
      </h1>
      <p className="feedback-admin__subtitle">
        In-app feedback from users. Status is read from the mirrored GitHub Issue —
        cogslog no longer owns triage state. Click a thumbnail to view full-size.
      </p>

      {/* Bulk "Refresh from GitHub" button — walks every mirrored row and
          re-caches gh_issue_state from the GH API. Disabled while running.
          The nightly cron (workflows/feedback-gh-state-sync.yml) does this
          automatically; the button forces an immediate sync. */}
      <div className="feedback-admin__toolbar">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleRefreshGhState}
          disabled={refreshing}
          data-testid="fa-refresh-gh-state"
        >
          {refreshing ? 'Refreshing from GitHub…' : 'Refresh state from GitHub'}
        </button>
        {refreshResult && (
          <span
            role="status"
            className={`feedback-admin__refresh-result feedback-admin__refresh-result--${refreshResult.ok ? 'ok' : 'err'}`}
            data-testid="fa-refresh-gh-state-result"
          >
            {refreshResult.text}
          </span>
        )}
      </div>

      {/* Filters */}
      <form
        className="feedback-admin__filters"
        onSubmit={handleApply}
        aria-label="Feedback filters"
      >
        <div className="field">
          <label className="label" htmlFor="fa-category">Category</label>
          <select
            id="fa-category"
            className="input"
            name="category"
            value={filters.category}
            onChange={handleFilterChange}
          >
            <option value="">Any</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="fa-gh-state">GitHub state</label>
          <select
            id="fa-gh-state"
            className="input"
            name="gh_state"
            value={filters.gh_state}
            onChange={handleFilterChange}
          >
            <option value="">Any</option>
            {GH_STATE_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="fa-date-from">Date from</label>
          <input
            id="fa-date-from"
            className="input"
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={handleFilterChange}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="fa-date-to">Date to</label>
          <input
            id="fa-date-to"
            className="input"
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={handleFilterChange}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="fa-user-email">User email (substring)</label>
          <input
            id="fa-user-email"
            className="input"
            type="text"
            name="user_email"
            value={filters.user_email}
            onChange={handleFilterChange}
            placeholder="e.g. alice"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="fa-org-id">Org ID</label>
          <input
            id="fa-org-id"
            className="input"
            type="number"
            name="org_id"
            value={filters.org_id}
            onChange={handleFilterChange}
            min="1"
            placeholder="e.g. 3"
          />
        </div>

        <div className="feedback-admin__filter-actions">
          <button type="submit" className="btn btn-primary">Apply</button>
          <button type="button" className="btn btn-ghost" onClick={handleReset}>Reset</button>
        </div>
      </form>

      {/* Status / error */}
      {error && (
        <p className="err-msg" role="status" aria-live="polite">{error}</p>
      )}
      {loading && (
        <div className="feedback-admin__loading" role="status" aria-live="polite">
          <img
            src="/logos/loader/cogslog-loader-light-sm.svg"
            width="32"
            height="72"
            alt="Loading..."
          />
        </div>
      )}

      {/* Table */}
      {!loading && rows.length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-state__title">No feedback found</div>
          <p className="empty-state__description">
            Try adjusting filters or wait for users to submit feedback.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="tbl-wrap">
          <table aria-label="Feedback submissions">
            <thead>
              <tr>
                <th scope="col">Timestamp</th>
                <th scope="col">User</th>
                <th scope="col">Category</th>
                <th scope="col">GH issue</th>
                <th scope="col">GH state</th>
                <th scope="col">Description</th>
                <th scope="col">Screenshot</th>
                <th scope="col">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr>
                    <td>
                      <span style={{ fontFamily: 'var(--font-m)', fontSize: '12px' }}>
                        {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                      </span>
                    </td>
                    <td>{row.user_email || '—'}</td>
                    <td>
                      <span className="badge">{row.category}</span>
                    </td>
                    <td data-testid={`fa-row-${row.id}-gh-issue`}>
                      {row.gh_issue_number ? (
                        <a
                          href={`https://github.com/COGSLOG/APP/issues/${row.gh_issue_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="feedback-admin__gh-link"
                          style={{ fontFamily: 'var(--font-m)' }}
                        >
                          #{row.gh_issue_number}
                        </a>
                      ) : (
                        <span className="feedback-admin__muted">—</span>
                      )}
                    </td>
                    <td data-testid={`fa-row-${row.id}-gh-state`}>
                      {row.gh_issue_number ? (
                        <span
                          className={`badge feedback-admin__gh-state feedback-admin__gh-state--${row.gh_issue_state || 'unknown'}`}
                          title={
                            row.gh_issue_state_synced_at
                              ? `Last synced ${new Date(row.gh_issue_state_synced_at).toLocaleString()}`
                              : 'Never synced'
                          }
                        >
                          {row.gh_issue_state
                            ? row.gh_issue_state.charAt(0).toUpperCase() + row.gh_issue_state.slice(1)
                            : 'Unknown'}
                        </span>
                      ) : (
                        <span className="badge feedback-admin__gh-state feedback-admin__gh-state--unmirrored">
                          Not mirrored
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="description-preview">
                        {truncate(row.description)}
                      </span>
                    </td>
                    <td>
                      {row.has_screenshot ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs feedback-admin__thumb-btn"
                          onClick={() => setScreenshotModalId(row.id)}
                          aria-label={`View screenshot for submission ${row.id}`}
                        >
                          <AuthImg
                            kind="screenshot"
                            adminApiPrefix={adminApiPrefix}
                            feedbackId={row.id}
                            alt={`Screenshot for submission ${row.id}`}
                            className="feedback-admin__thumb"
                          />
                        </button>
                      ) : (
                        <span>—</span>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        aria-expanded={expandedId === row.id}
                        aria-controls={`expand-row-${row.id}`}
                        onClick={() => toggleExpand(row.id)}
                      >
                        {expandedId === row.id ? 'Collapse' : 'Expand'}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded detail row */}
                  {expandedId === row.id && (
                    <tr id={`expand-row-${row.id}`} className="feedback-admin__expand-row">
                      <td colSpan={7}>
                        <div
                          className={
                            row.linked_entity_type
                              ? 'feedback-admin__expand-content feedback-admin__expand-content--with-sidebar'
                              : 'feedback-admin__expand-content'
                          }
                        >
                          <div className="feedback-admin__expand-main">
                            {row.gh_issue_number ? (
                              <div className="feedback-admin__expand-section">
                                <GithubThreadPanel
                                  adminApiPrefix={adminApiPrefix}
                                  feedbackId={row.id}
                                  issueNumber={row.gh_issue_number}
                                />
                              </div>
                            ) : null}
                            <div className="feedback-admin__expand-section">
                              <strong>Breadcrumbs</strong>
                              <pre className="feedback-admin__json">
                                {JSON.stringify(row.breadcrumbs, null, 2)}
                              </pre>
                            </div>
                            <div className="feedback-admin__expand-section">
                              <strong>Browser info</strong>
                              <pre className="feedback-admin__json">
                                {JSON.stringify(row.browser_info, null, 2)}
                              </pre>
                            </div>
                            {Array.isArray(row.extra_attachments) && row.extra_attachments.length > 0 && (
                              <div className="feedback-admin__expand-section">
                                <strong>Attachments</strong>
                                <div className="feedback-admin__attachment-list">
                                  {row.extra_attachments.map((_, n) => (
                                    <AuthImg
                                      key={n}
                                      kind="attachment"
                                      adminApiPrefix={adminApiPrefix}
                                      feedbackId={row.id}
                                      attachmentIndex={n}
                                      alt={`Attachment ${n + 1} for submission ${row.id}`}
                                      className="feedback-admin__thumb"
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {row.linked_entity_type ? (
                            <LinkedEntitySidebar
                              adminApiPrefix={adminApiPrefix}
                              feedbackId={row.id}
                              onRetry={handleRetryCrmSync}
                            />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > DEFAULT_PER_PAGE && (
        <Pagination
          page={page}
          perPage={DEFAULT_PER_PAGE}
          total={total}
          onPageChange={setPage}
        />
      )}

      {/* Full-size screenshot modal */}
      {screenshotRow && (
        <Modal
          isOpen={!!screenshotModalId}
          onClose={() => setScreenshotModalId(null)}
          title={`Screenshot — submission #${screenshotRow.id}`}
          size="large"
        >
          <AuthImg
            kind="screenshot"
            adminApiPrefix={adminApiPrefix}
            feedbackId={screenshotRow.id}
            alt={`Full-size screenshot for submission ${screenshotRow.id}`}
            className="feedback-admin__screenshot-full"
          />
        </Modal>
      )}
    </div>
  );
}

export default FeedbackAdmin;
